import React, { useCallback, useMemo } from 'react';
import { Col, Form, Row, Select } from 'antd';
import type { Rule } from 'antd/es/form';
import { useIntl } from 'react-intl';

import { FormFloatingCurrencyItem } from '../form-floating-currency-item';
import formFieldStyles from '../form-field/styles.module.scss';
import type { InstalmentSummary } from '../../utils/payment-math';

import styles from './styles.module.scss';

const INSTALLMENT_PERIOD_VALUES = [4, 6, 13] as const;

export interface InstalmentPlanBlockProps {
  downPaymentRules: Rule[];
  instalmentSummary: InstalmentSummary | null;
  isSubmitting: boolean;
  formItemClassName: string;
  onOpenPaymentSchedule: () => void;
}

export function InstalmentPlanBlock({
  downPaymentRules,
  instalmentSummary,
  isSubmitting,
  formItemClassName,
  onOpenPaymentSchedule,
}: InstalmentPlanBlockProps): React.ReactElement {
  const intl = useIntl();
  const nextPaymentText = instalmentSummary?.nextPaymentLabel ?? '';

  const instalmentPeriodOptions = useMemo(
    () =>
      INSTALLMENT_PERIOD_VALUES.map((value) => ({
        value,
        label: intl.formatMessage({ id: `certificates.payment.instalmentMonths.${value}` }),
      })),
    [intl],
  );

  const handleSeeScheduleClick = useCallback(() => {
    onOpenPaymentSchedule();
  }, [onOpenPaymentSchedule]);

  return (
    <div className={styles.section}>
      <Row gutter={[16, 16]} className={styles.fieldsRow}>
        <Col xs={24} md={12}>
          <FormFloatingCurrencyItem
            name="downPaymentAmount"
            label={intl.formatMessage({ id: 'certificates.payment.deposit' })}
            rules={downPaymentRules}
            formItemClassName={formItemClassName}
            placeholder={intl.formatMessage({ id: 'certificates.payment.amountPlaceholder' })}
            inputMode="decimal"
            disabled={isSubmitting}
          />
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            className={[formFieldStyles.formItem, formItemClassName].filter(Boolean).join(' ')}
          >
            <div className={formFieldStyles.inputWrapper}>
              <span className={formFieldStyles.innerLabel}>
                {intl.formatMessage({ id: 'certificates.payment.instalmentsPeriod' })}
              </span>
              <Form.Item
                name="instalmentMonths"
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: 'certificates.payment.instalmentPeriodRequired',
                    }),
                  },
                ]}
                noStyle
              >
                <Select
                  className={[formFieldStyles.select, styles.fullWidthSelect]
                    .filter(Boolean)
                    .join(' ')}
                  options={instalmentPeriodOptions}
                  getPopupContainer={(node) => node.parentElement ?? document.body}
                  disabled={isSubmitting}
                />
              </Form.Item>
            </div>
          </Form.Item>
        </Col>
      </Row>

      {nextPaymentText && (
        <p className={styles.nextPaymentRow}>
          <span className={styles.nextPaymentText}>
            {intl.formatMessage({ id: 'certificates.payment.nextPaymentPrefix' })} {nextPaymentText}
          </span>{' '}
          <button type="button" className={styles.scheduleLink} onClick={handleSeeScheduleClick}>
            {intl.formatMessage({ id: 'certificates.payment.seeSchedule' })}
          </button>
        </p>
      )}
    </div>
  );
}
